import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useTranslation } from "react-i18next";
import moment from "moment";

declare global {
  interface Window {
    d3: any;
  }
}

interface ScatterChartProps {
  scatterData: any;
  lineData: any;
  height?: string | number;
  width?: string | number;
  containerHeight?: string | number;
  containerWidth?: string | number;
  customLegendItems?: any;
  yAxisLabelText?: string;
  xAxisLabelText?: string;
  title?: string;
  tooltip?: any;
  ageMultiple?: number;
  markersSize?: number[];
  horizontalLegendAlign: "right" | "left" | "center" | undefined;
  animation?: boolean;
  showLegend?: boolean;
  handlerSeriesFillColor?: (scatteritem: any) => string;
}

const formatDate = (date: string) =>
  (date && moment(date).format("DD/MM/YYYY")) || "";

const ScatterChart: React.FC<ScatterChartProps> = ({
  handlerSeriesFillColor = (data) =>
    data.type === "single" && data.isFeatHost ? "#EC0190" : "#3791e3",
  scatterData = [],
  lineData = [],
  height = 500,
  width = "100%",
  containerHeight,
  containerWidth,
  customLegendItems = [],
  yAxisLabelText,
  xAxisLabelText,
  title,
  tooltip,
  ageMultiple = 1,
  markersSize = [6, 0],
  horizontalLegendAlign = "left",
  animation = true,
  showLegend = true,
}: ScatterChartProps) => {
  const { t } = useTranslation("radar");

  const getXAxis = () => {
    const xAxis = [];

    const oldestMonth = scatterData
      ? scatterData[scatterData.length - 1]?.x
      : 0;

    if (oldestMonth < -6) {
      xAxis.push({
        x: -6 * ageMultiple,
        strokeDashArray: 0,
        borderColor: "#6d234a",
        label: {
          borderColor: "#6d234a",
          orientation: "horizontal",
          textAnchor: "right",
          style: {
            color: "#fff",
            background: "#6d234a",
          },
          text: t("pages.ArtistProfile.sixMonths"),
        },
      });
    }
    if (oldestMonth < -18) {
      xAxis.push({
        x: -18 * ageMultiple,
        strokeDashArray: 0,
        borderColor: "#37b1e4",
        label: {
          borderColor: "#37b1e4",
          orientation: "horizontal",
          textAnchor: "right",
          style: {
            color: "#fff",
            background: "#37b1e4",
          },
          text: t("pages.ArtistProfile.eighteenMonths"),
        },
      });
    }

    if (oldestMonth < -36) {
      xAxis.push({
        x: -36 * ageMultiple,
        strokeDashArray: 0,
        borderColor: "#4a1f67",
        label: {
          borderColor: "#4a1f67",
          orientation: "horizontal",
          textAnchor: "right",
          style: {
            color: "#fff",
            background: "#4a1f67",
          },
          text: t("pages.ArtistProfile.thirtySixMonths"),
        },
      });
    }
    if (oldestMonth < -72) {
      xAxis.push({
        x: -72 * ageMultiple,
        strokeDashArray: 0,
        borderColor: "#4a1a10",
        label: {
          borderColor: "#4a1a10",
          orientation: "horizontal",
          textAnchor: "right",
          style: {
            color: "#fff",
            background: "#4a1a10",
          },
          text: t("pages.ArtistProfile.seventyTwoMonths"),
        },
      });
    }

    return xAxis;
  };

  const series: any = [
    {
      type: "scatter",
      data: scatterData.map((data: any) => ({
        ...data,
        fillColor: handlerSeriesFillColor(data),
      })),
      name: "scatter",
    },
    {
      type: "line",
      data: lineData,
      color: "#E4BC5C",
      name: "line",
    },
  ];

  const optionsa: ApexOptions = {
    chart: {
      height: height,
      width: width,
      // selection: { enabled: false },
      events: {
        mounted: () => {
          // const scatter = document.getElementsByClassName(
          //   "apexcharts-line-series"
          // )[0];
          // const line = document.getElementsByClassName(
          //   "apexcharts-inner apexcharts-graphical"
          // )[0];
          // line?.insertBefore(scatter, null);
        },
      },
      toolbar: {
        show: false,
      },
      animations: {
        enabled: animation,
      },
    },
    dataLabels: {
      enabled: false,
    },

    fill: {
      type: "solid",
    },
    markers: {
      size: markersSize,
      strokeWidth: 0,
    },
    title: {
      text: title,
      style: { color: "white" },
      align: "left",
    },
    colors: ["#3791e3", "#EC0190", "#3791e3"],
    tooltip: (tooltip?.points?.length > 0 && {
      custom: ({ dataPointIndex }: any) => {
        const pointScatter = scatterData[dataPointIndex];
        if (!pointScatter) return null;

        const {
          name = "",
          releaseDate,
          ageInMonths = "",
          popularity = "",
          type = "",
          artistName = "",
        } = pointScatter || {};

        return `
          <div class="apexcharts-tooltip-title" style="font-size:12px">${
            tooltip.title
          }</div>
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <div class="apexcharts-tooltip-text" style="font-size:12px">           
              <div class="apexcharts-tooltip-goals-group">
                <span class="apexcharts-tooltip-text-goals-label">${
                  tooltip.name
                }</span>
              <span class="apexcharts-tooltip-text-goals-value">${name}</span>
            </div>
            <div class="apexcharts-tooltip-goals-group">
              <span class="apexcharts-tooltip-text-goals-label">${
                tooltip.release
              }</span>
              <span class="apexcharts-tooltip-text-goals-value">${formatDate(
                releaseDate
              )}</span>
            </div>
            <div class="apexcharts-tooltip-goals-group">
              <span class="apexcharts-tooltip-text-goals-label">${
                tooltip.ageInMonths
              }</span>
              <span class="apexcharts-tooltip-text-goals-value">${ageInMonths}</span>
            </div>
            <div class="apexcharts-tooltip-goals-group">
              <span class="apexcharts-tooltip-text-goals-label">${
                tooltip.popularity
              }</span>
              <span class="apexcharts-tooltip-text-goals-value">${popularity}</span>
            </div>
            ${
              tooltip.type
                ? `
            <div class="apexcharts-tooltip-goals-group">
              <span class="apexcharts-tooltip-text-goals-label">${tooltip.type}</span>
              <span class="apexcharts-tooltip-text-goals-value">${type}</span>
            </div>
          `
                : ""
            }
            ${
              tooltip.artistName
                ? `
              <div class="apexcharts-tooltip-goals-group">
                <span class="apexcharts-tooltip-text-goals-label">${tooltip.artistName}</span>
                <span class="apexcharts-tooltip-text-goals-value">${artistName}</span>
              </div>
              `
                : ""
            }
           
          </div>
        </div>
      `;
      },
      shared: false,
      enabled: true,
    }) || { enabled: false },
    legend: {
      position: "bottom",
      customLegendItems: customLegendItems,
      horizontalAlign: horizontalLegendAlign,
      labels: {
        colors: ["#fff"],
      },
      show: showLegend,
    },
    stroke: {
      curve: "smooth",
    },
    annotations: {
      xaxis: getXAxis(),
    },
    yaxis: {
      labels: {
        formatter: (val: any) => {
          return val?.toFixed(0) || "";
        },
        style: {
          colors: "#fff",
        },
      },

      title: {
        text: yAxisLabelText,
        style: {
          color: "#fff",
        },
      },
    },
    xaxis: {
      type: "numeric",
      max: 0,
      labels: {
        style: {
          colors: "#fff",
        },
        formatter: (val: any) => {
          return (val * -1).toFixed(0) || "";
        },
      },
      title: {
        text: xAxisLabelText,
        style: {
          color: "#fff",
        },
      },
    },
  };

  return (
    <Chart
      options={optionsa}
      series={series}
      height={containerHeight}
      type="line"
      width={containerWidth}
    />
  );
};

export default ScatterChart;
